import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Edit, Trash2, X } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { LeaveModal } from "@/components/AnnualLeaveTab";
import { LeaveDocs } from "@/components/leave/LeaveDocs";
import { LeaveHistory } from "@/components/leave/LeaveHistory";

const TUR = { yillik: "Yıllık İzin", ucretli_izin: "Ücretli İzin", ucretsiz_izin: "Ücretsiz İzin", rapor: "Rapor" };

export function LeaveRecordDialog({ leaveId, personnelId, onClose, onChanged }) {
  const [leave, setLeave] = useState(null);
  const [summary, setSummary] = useState([]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hTick, setHTick] = useState(0);

  useEffect(() => {
    api.get(`/annual-leave?personnel_id=${personnelId}`)
      .then((r) => setLeave((r.data.rows || []).find((x) => x.id === leaveId) || false))
      .catch(() => setLeave(false));
    api.get("/annual-leave/summary").then((r) => setSummary(r.data.rows || [])).catch(() => setSummary([]));
  }, [leaveId, personnelId]);

  const remove = async () => {
    if (!window.confirm(`${TUR[leave.tur] || "İzin"} kaydı silinecek (${fmtDate(leave.baslangic)} → ${fmtDate(leave.bitis)}). Onaylıyor musunuz?`)) return;
    setBusy(true);
    try {
      await api.delete(`/annual-leave/${leave.id}`);
      toast.success("Kayıt silindi");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally { setBusy(false); }
  };

  if (editing && leave) {
    return <LeaveModal editing={leave} personnelId={personnelId} personnel={summary}
      onClose={() => setEditing(false)} onSaved={() => { onChanged?.(); onClose(); }} />;
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="leave-record-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-display font-extrabold text-slate-900 flex items-center gap-2 text-sm">
            <CalendarDays size={16} className="text-teal-600" /> İzin / Rapor Kaydı
          </h3>
          <button onClick={onClose} data-testid="leave-record-close" className="w-7 h-7 grid place-items-center hover:bg-slate-100 rounded"><X size={14} /></button>
        </div>
        <div className="p-4 text-sm space-y-3">
          {leave === null && <div className="text-xs text-slate-500">Yükleniyor...</div>}
          {leave === false && <div className="text-xs text-rose-600">Kayıt bulunamadı</div>}
          {leave && (
            <>
              <div data-testid="leave-record-info">
                <div className="font-bold">{TUR[leave.tur] || leave.tur} · {leave.gun_sayisi} gün</div>
                <div className="text-slate-600">{fmtDate(leave.baslangic)} → {fmtDate(leave.bitis)}</div>
                {leave.aciklama && <div className="text-xs italic text-slate-500">{leave.aciklama}</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditing(true)} disabled={busy} data-testid="leave-record-edit"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-2 rounded-md disabled:opacity-50">
                  <Edit size={13} /> Düzenle
                </button>
                <button onClick={remove} disabled={busy} data-testid="leave-record-delete"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-2 rounded-md disabled:opacity-50">
                  <Trash2 size={13} /> {busy ? "Siliniyor..." : "Sil"}
                </button>
              </div>
              <div className="border-t border-slate-200 pt-3"><LeaveDocs leaveId={leave.id} onChanged={() => setHTick((t) => t + 1)} /></div>
              <div className="border-t border-slate-200 pt-3"><LeaveHistory leaveId={leave.id} refreshKey={hTick} /></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
